// =============================================================================
// Server-safe system component resolver
// =============================================================================
//
// In the legacy portal, themeRegistry transitively imported
// {@code BlockRenderer.tsx} ('use client') which made the whole module
// client-only — broken for system page routes that render on the
// server. The fix there was a parallel server-safe resolver that read
// theme bundles directly without dragging in the renderer.
//
// In the runtime ({@code @akademi/site-builder-runtime}), the
// registry in {@link ./themeRegistry} doesn't import any client-only
// module — it's a plain Map. Both the registry and this file are
// server-safe, so this module's only job is to expose the resolver
// under a stable name so legacy call sites that imported from
// {@code @/lib/platform/resolveSystem} keep working after the move.
//
// {@code SystemKind} is left open (string) to match the runtime's
// stringly-typed system map; hosts can constrain it with their own
// union if they prefer compile-time exhaustiveness.

import type { SystemComponent } from './types';
import { resolveSystem as registryResolveSystem } from './themeRegistry';

/** Open string union for system kinds. Hosts narrow as needed. */
export type SystemKind = string;

/**
 * Returns the themed component if present, OR the default theme's
 * version if present, OR {@code undefined} — meaning "no theme
 * override; the route should render its hardcoded fallback."
 */
export function resolveSystem(
  themeId: string | null | undefined,
  kind: SystemKind,
): SystemComponent | undefined {
  return registryResolveSystem(themeId, kind);
}
